import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import LinearProgress from '@mui/material/LinearProgress';
import { Box, Card, Grid, Typography } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import DropdownCardMenu from 'common/components/DropdownCardMenu';
import EmptyState from 'common/components/EmptyState';
import ProviderImage from 'common/components/ProviderImage';

import EmptySuppliers from 'resources/EmptySuppliers.svg';

import { VISIBLE_USER_FIELDS } from '../constants';
import { getFieldLabel } from '../helpers';

const IntegrationsList = ({
    canDelete,
    canModify,
    handleClickCreate,
    integrations,
    isFetching,
    prepareDeleteKey,
    providersDictionary,
}) => {
    const { t } = useTranslation();

    if (isFetching)
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );

    if (isEmpty(integrations))
        return (
            <EmptyState
                canModify={canModify}
                caption={t(
                    'Add your suppliers. You will see a complete list here where you can access them',
                )}
                classNameCard="border-0"
                onClick={handleClickCreate}
                srcImage={EmptySuppliers}
                sxCard={{ border: 0, boxShadow: 'none' }}
                textButton={t('Add')}
                title={t('Your directory of suppliers')}
            />
        );

    return (
        <Grid container>
            {integrations.map((integration) => {
                if (!integration) return null;
                const provider = providersDictionary?.[integration.type];

                return (
                    <Grid key={integration.id} size={{ lg: 6, sm: 9, xs: 18 }}>
                        <Card>
                            <Card.Header
                                sx={{
                                    alignItems: 'flex-start',
                                    display: 'flex',
                                    gap: 2,
                                    p: 1,
                                }}
                            >
                                <ProviderImage
                                    provider={provider}
                                    style={{
                                        flex: '1 1 auto',
                                        maxHeight: '60px',
                                        minWidth: '60px',
                                        objectFit: 'contain',
                                    }}
                                />

                                <DropdownCardMenu
                                    items={[
                                        {
                                            handleClick: () =>
                                                prepareDeleteKey(
                                                    integration.id,
                                                ),
                                            icon: (
                                                <DeleteIcon fontSize="small" />
                                            ),
                                            label: t('Delete'),
                                            visible: canDelete,
                                        },
                                    ]}
                                    visible={canDelete}
                                />
                            </Card.Header>

                            <Card.Body
                                sx={{
                                    display: 'flex',
                                    flexDirection: { sm: 'row', xs: 'column' },
                                    justifyContent: 'space-between',
                                }}
                            >
                                {provider?.fields?.map((field) => {
                                    if (!VISIBLE_USER_FIELDS[field])
                                        return null;

                                    return (
                                        <Box key={field}>
                                            <Typography variant="body2">
                                                <span
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    {getFieldLabel(field)}:
                                                </span>{' '}
                                                {integration[field] || '*****'}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Card.Body>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

IntegrationsList.propTypes = {
    canDelete: PropTypes.bool,
    canModify: PropTypes.bool,
    handleClickCreate: PropTypes.func,
    integrations: PropTypes.array,
    isFetching: PropTypes.bool,
    prepareDeleteKey: PropTypes.func,
    providersDictionary: PropTypes.object,
};

export default IntegrationsList;
